import { useQuery } from '@apollo/client';
import { AliPlayer, ECryptoType } from '../../components/AliPlayer';
import {
  EResourceType,
  ERoleType,
  GetVideoCoursePageDataDocument,
  GetVideoLessonPlayInfoDocument,
} from '../../services/graphql/types/graphql';
import { useQueryState } from '../../hooks/useQueryState';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { IdentityContext } from '../App/identity.context';
import { useStoreContext } from '../../factorys/useStoreContext';
import { isNull } from 'lodash';
import { Loader } from '../../components/Loader';
import MuYiTongXueLogo from '../../assets/muyitongxue-logo--new-black.png';
import JingguiLogo from '../../assets/jinggui-logo.png';
import { CasdoorContext } from '../App/casdoor.context';
import cx from 'classnames';

import { isMobileOnly, isIOS } from 'react-device-detect';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import { studentAvatarImageUrl } from '../../utils/studentAvatar';
import { WatermarkedMediaOverlay } from '../../components/WatermarkedMediaOverlay';

const VIDEO_WATERMARK_ELEMENT_ID = 'video-course-watermark';
const VIDEO_PLAYER_ELEMENT_ID = 'video-course-aliplayer';

export const VideoCoursePage = () => {
  const { user } = useContext(CasdoorContext);

  const orgName = import.meta.env.VITE_CASDOOR_ORG_NAME || '';
  const logo = orgName === 'jinggui' ? JingguiLogo : MuYiTongXueLogo;

  const [courseId] = useQueryState('courseId', '');
  if (!courseId) {
    throw new Error('courseId is required');
  }

  const refScrollContainer = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (isMobileOnly && refScrollContainer.current) {
      disableBodyScroll(refScrollContainer.current);
    }

    return () => {
      clearAllBodyScrollLocks();
    };
  }, []);

  const { uac } = useStoreContext(IdentityContext);
  const [permission, setPermission] = useState<boolean | null>(null);
  useEffect(() => {
    setPermission(
      uac.some(
        (item) =>
          item.resourceType === EResourceType.VideoCourse &&
          item.resourceId === courseId &&
          item.role === ERoleType.Student,
      ),
    );
  }, [uac, courseId]);

  const {
    data: courseData,
    loading: courseLoading,
  } = useQuery(GetVideoCoursePageDataDocument, {
    variables: { courseId },
  });

  const [activeLessonId, setActiveLessonId] = useState<string | null>(null);
  const [videoModalOpen, setVideoModalOpen] = useState(false);

  const {
    data: videoPlayInfoData,
    loading: videoPlayInfoLoading,
  } = useQuery(GetVideoLessonPlayInfoDocument, {
    variables: { lessonId: activeLessonId! },
    skip: !videoModalOpen || !activeLessonId,
  });

  const watermark = useMemo(
    () => user?.nickname ?? user?.name ?? '',
    [user?.nickname, user?.name],
  );

  const handleLessonClick = (lessonId: string) => {
    setActiveLessonId(lessonId);
    setVideoModalOpen(true);
  };

  const handleCloseVideoModal = () => {
    setVideoModalOpen(false);
  };

  const renderLessonList = () =>
    courseData?.course.lessons.map((lesson) => (
      <div
        className="flex h-11 w-full flex-shrink-0 cursor-pointer items-center gap-x-3 border-b border-white px-2 text-[14px]"
        key={lesson.lessonId}
        onClick={() => handleLessonClick(lesson.lessonId)}
      >
        <div className="mx-2 flex w-[10px] flex-shrink-0 items-center justify-center">
          <div
            className={cx('h-[5px] w-[5px] rounded-full bg-white', {
              '!h-[10px] !w-[10px] !bg-[#F3382F]': activeLessonId === lesson.lessonId,
            })}
          />
        </div>
        {lesson.priority}. {lesson.name}
      </div>
    ));

  if (isNull(permission)) {
    return <Loader fullscreen source="VideoCoursePage" />;
  }

  if (permission === false) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        您还未购买课程，如果已经购买请联系客服
      </div>
    );
  }

  const videoModal = (
    <WatermarkedMediaOverlay
      open={videoModalOpen}
      onClose={handleCloseVideoModal}
      watermark={watermark}
      targetElementId={VIDEO_WATERMARK_ELEMENT_ID}
    >
      <div className="aspect-video w-full max-h-[80vh] overflow-hidden rounded-lg bg-black">
        {videoPlayInfoLoading || !videoPlayInfoData ? (
          <div className="flex h-full min-h-[240px] items-center justify-center">
            <Loader source="VideoCoursePlayer" />
          </div>
        ) : (
          <AliPlayer
            playerId={VIDEO_PLAYER_ELEMENT_ID}
            videoId={videoPlayInfoData.videoLessonPlayInfo.videoId}
            playAuth={videoPlayInfoData.videoLessonPlayInfo.playAuth}
            cryptoType={isIOS ? ECryptoType.None : ECryptoType.Aliyun}
            disableFullscreen
            autoplay
          />
        )}
      </div>
    </WatermarkedMediaOverlay>
  );

  if (isMobileOnly) {
    return (
      <>
        <div className="flex h-screen w-screen flex-col gap-2 overflow-hidden bg-[#ece9e2] p-2">
          <div className="flex w-full flex-col gap-2 overflow-hidden rounded-xl bg-white px-2 py-2">
            <div className="flex items-center">
              <img
                className="h-12 w-12 rounded-full"
                src={studentAvatarImageUrl(undefined, user?.photo, {
                  resize: { w: 64, h: 64, m: 'mfit' },
                })}
                alt=""
              />
              <div className="my-auto ml-[9px] flex flex-col font-semibold">
                <div className="text-[20px]">{user?.nickname ?? user?.name}</div>
              </div>
              <div className="ml-auto h-12 w-12">
                <img className="object-contain" src={logo} alt="" />
              </div>
            </div>
          </div>
          <div className="h-28 overflow-hidden rounded-xl bg-white text-[16px]">
            <img
              className="h-full w-full object-cover"
              src={courseData?.course.bannerImageUrl ?? ''}
              alt=""
            />
          </div>
          <div ref={refScrollContainer} className="flex-1 overflow-auto rounded-xl bg-[#DFDBD1]">
            {courseLoading ? (
              <div className="flex h-40 items-center justify-center">
                <Loader source="VideoCourseLessons" />
              </div>
            ) : (
              renderLessonList()
            )}
          </div>
        </div>
        {videoModal}
      </>
    );
  }

  return (
    <>
      <div className="flex h-screen w-screen gap-4 bg-[#ece9e2] p-4">
        <div className="flex h-full w-1/4 max-w-[350px] flex-col gap-4">
          <div className="flex flex-col rounded-xl bg-white px-3 py-6">
            <div className="mx-auto h-40 w-40">
              <img className="object-contain" src={logo} alt="" />
            </div>
            <div className="mt-5 flex items-center">
              <img
                className="h-12 w-12 rounded-full"
                src={studentAvatarImageUrl(undefined, user?.photo, {
                  resize: { w: 64, h: 64, m: 'mfit' },
                })}
                alt=""
              />
              <div className="my-auto ml-[9px] flex flex-col font-semibold">
                <div className="text-[20px]">{user?.nickname ?? user?.name}</div>
              </div>
            </div>
          </div>
          <div className="h-28 overflow-hidden rounded-xl bg-white text-[16px]">
            <img
              className="h-full w-full object-cover"
              src={courseData?.course.bannerImageUrl ?? ''}
              alt=""
            />
          </div>
          <div className="flex-1 overflow-auto rounded-xl bg-[#DFDBD1]">
            {courseLoading ? (
              <div className="flex h-40 items-center justify-center">
                <Loader source="VideoCourseLessons" />
              </div>
            ) : (
              renderLessonList()
            )}
          </div>
        </div>
        <div className="flex flex-1 items-center justify-center overflow-hidden rounded-xl bg-white p-8 text-gray-500">
          {activeLessonId ? '点击左侧课次可在弹窗中观看视频' : '请从左侧选择课次观看视频'}
        </div>
      </div>
      {videoModal}
    </>
  );
};
